<template>
  <v-dialog v-model="dialog" max-width="400px" class="delete-item-dialog" v-on:keydown="handleKeydown($event)">
    <template v-if="!hideIcon" v-slot:activator="{ props }">
      <slot :props="props">
        <v-tooltip location="top" open-delay="500">
          <template v-slot:activator="{ props: onHoverProps }">
            <v-icon
              color="white"
              size="small"
              v-bind="{ ...props, ...onHoverProps }"
            >
              mdi-delete-outline
            </v-icon>
          </template>
          <template #default>
            <div>
              <p class="text-body-2 mb-1">Delete</p>
              <p class="mb-0">
                <code>ALT/⌥ + BACKSPACE</code> - Delete selected event
              </p>
            </div>
          </template>
        </v-tooltip>
      </slot>
    </template>
    <v-card class="pa-4">
      <v-card-title class="pa-0">
        <span class="text-h5">Confirm deletion</span>
      </v-card-title>
      <v-card-text class="pa-0 mt-6" v-if="itemType === 'Card'"> Are you sure you want to delete this card?</v-card-text>
      <v-card-text class="pa-0 mt-6" v-else-if="Array.isArray(selectedEventIds) && selectedEventIds.length > 1">
        Are you sure you want to delete these {{ selectedEventIds.length }} Events from the workflow including all their cards?
      </v-card-text>
      <v-card-text class="pa-0 mt-6" v-else> Are you sure you want to delete this {{ itemType }} from the workflow including all its
        cards?
      </v-card-text>
      <v-card-actions class="justify-end pa-0 mt-6" style="padding: 0 !important;">
        <v-btn rounded="lg"
               size="large"
               variant="outlined"
               @click="closeDialog()">Cancel
        </v-btn>
        <v-btn
          rounded="lg"
          size="large"
          variant="flat"
          color="primary"
          :class="{
            'confirm-delete-card': itemType == 'Card',
            'confirm-delete-event': itemType != 'Card'
          }"
          :loading="loading"
          @click="deleteItem()"
          ref="deleteButton"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import workflowApi from '@/api/workflowApi'
import { mapState, mapGetters } from 'vuex'
import { DomainModelRole } from '@/constants/cardTypes'

export default {
  props: [
    'itemType',
    'card',
    'hideIcon',
    'selectedEventId',
    'selectedEventIds',
    'isMultiSelection'
  ],
  data () {
    return {
      dialog: false,
      loading: false
    }
  },
  methods: {
    async deleteItem () {
      this.loading = true

      // If multi-selection, emit event for parent to handle
      const hasMultipleEvents = Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1
      if (hasMultipleEvents) {
        this.$emit('delete-multiple-events', this.selectedEventIds)
        this.loading = false
        this.closeDialog()
        return
      }

      const eventId = this.itemType === 'Card' ? (this.card?.eventId || this.eventId) : this.eventId
      const event = this.svgGraphData[eventId]

      if (this.itemType === 'Card') {
        // Remove the card
        if ([DomainModelRole.COMMAND, DomainModelRole.READ_MODEL].includes(this.card?.cardType?.domainModelRole)) {
          await workflowApi.deleteCardAndModel(event, this.card, this.svgGraphData, this.workflow)
        } else {
          const workflowId = this.$route.params.id
          await workflowApi.deleteCard(workflowId, { cardId: this.card.id })
        }
      } else {
        if (this.eventCanBeDeleted) {
          const nextActiveEventId = this.getNextActiveEventIdAfterDelete(event)
          await workflowApi.deleteEventFromWorkflow(event.id, this.svgGraphData, this.svgLanesData, this.svgGroupsData, this.workflow)
          if (nextActiveEventId && this.svgGraphData[nextActiveEventId]) {
            this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id + '/' + nextActiveEventId }).catch(() => {
            })
          } else {
            this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id }).catch(() => {
            })
          }
        }
      }
      this.loading = false
      this.closeDialog()
    },
    getNextActiveEventIdAfterDelete (event) {
      const nextActiveEvent = this.workflow.domainEvents.find(ev => ev.parents[0] === event.id)
      if (nextActiveEvent?.id && this.svgGraphData[nextActiveEvent.id].x <= event.x + 160) {
        // use the child if it's close enough (next slot)
        return nextActiveEvent.id
      }
      if (event.parents[0]) {
        // use the parent if it exists
        return event.parents[0]
      }
      return null
    },
    openDialog () {
      // Always allow opening for multi-selection (check selectedEventIds directly)
      const hasMultipleEvents = Array.isArray(this.selectedEventIds) && this.selectedEventIds.length > 1
      if (hasMultipleEvents) {
        this.dialog = true
        this.$nextTick(() => this.$refs.deleteButton?.$el.focus())
        return
      }

      // For single selection, check if event can be deleted
      if (!this.eventCanBeDeleted) {
        return
      }
      this.dialog = true
      this.$nextTick(() => this.$refs.deleteButton?.$el.focus())
    },
    closeDialog () {
      this.dialog = false
    },
    handleKeydown (event) {
      if (event.key === 'Escape') {
        this.closeDialog()
      }
      if (event.key === 'Enter') {
        this.deleteItem()
      }
      event.preventDefault()
      event.stopPropagation()
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    }),
    ...mapGetters({
      svgGraphData: 'svgGraphData/svgGraphData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGroupsData: 'svgGraphData/svgGroupsData'
    }),
    eventCanBeDeleted () {
      // Any event with a valid id can now be deleted; children of the
      // removed event are re-parented to its parent (which may now hold
      // multiple parallel children).
      return !!this.eventId && !!this.workflow?.domainEvents?.some(ev => ev.id === this.eventId)
    },
    eventId () {
      return this.$route.params.eventId || this.selectedEventId
    }
  },
  watch: {
    dialog (val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.deleteButton?.$el?.focus()
        })
        this.$emit('open')
      }
    }
  }
}
</script>

<style scoped>
</style>
